import React, { type FC } from "react";
import { LuHeadset } from "react-icons/lu";
import { useI18next } from "gatsby-plugin-react-i18next";

import {
  CHAT_BRAND,
  customerTitleOf,
  formatWhen,
  previewOf,
  savedTitleOf,
  senderIdOf,
} from "@components/chat/chat_helpers";
import { useCustomerCareStore } from "@stores/customer_care";
import { ccAttachmentUrl, type CcConversation } from "@utilities/customer-care";

/**
 * One conversation in the Chat history: the conversation's title
 * (the topic the customer opened it with), the profile image of whoever
 * answered (the support headset disc before anyone has), and the latest
 * message as the preview, prefixed with its sender ("You:" for the
 * customer, the agent's or bot's name otherwise) so the responder stays
 * visible now that the title no longer carries their name. A conversation
 * without a title falls back to the customer's first message (customerTitleOf),
 * then the responder's name, then "Support team".
 */
const ChatCard: FC<{ conversation: CcConversation; onOpen: () => void }> = ({
  conversation,
  onOpen,
}) => {
  const { t, language: locale } = useI18next();
  const profileId = useCustomerCareStore(state => state.profile?.userId);
  const users = useCustomerCareStore(state => state.users);
  const thread = useCustomerCareStore(
    state => state.threads[String(conversation.id)],
  );
  // The latest message: from the loaded detail when there is one (it names
  // its senders), else the list item's lastMessage.
  const last =
    thread !== undefined && thread.messages.length > 0
      ? thread.messages[thread.messages.length - 1]
      : conversation.lastMessage;
  const senderId = senderIdOf(last);
  const isMine =
    senderId !== undefined &&
    profileId !== undefined &&
    senderId === String(profileId);
  // Who answered: the last message's sender when it is not the customer
  // (bot or agent), else the conversation's assigned agent.
  const responder =
    senderId !== undefined && !isMine
      ? (thread?.senders[senderId] ?? users[senderId])
      : undefined;
  const who = responder ?? conversation.agent ?? undefined;
  const whoName = (who?.firstName ?? "").trim();
  const conversationTitle = savedTitleOf(conversation.title, locale, t);
  // The detail is loaded in the background (refreshList), so the first
  // message shows up on its own shortly after the list does.
  const firstLine = customerTitleOf(thread?.messages, profileId, locale, t);
  const title =
    conversationTitle !== ""
      ? conversationTitle
      : firstLine !== ""
        ? firstLine
        : whoName !== ""
          ? whoName
          : t("chat_support_team", CHAT_BRAND);
  // Who wrote the previewed message. The responder's name is only worth the
  // space when the title does not already say it.
  const previewPrefix = isMine
    ? t("chat_you")
    : whoName !== "" && whoName !== title
      ? whoName
      : "";
  const image = (who?.profileImage ?? "").trim();
  const preview = previewOf(
    last !== null && last !== undefined
      ? { ...conversation, lastMessage: last }
      : conversation,
    t("chat_attachment"),
  );
  const when = last?.creationTime ?? conversation.creationTime;
  const unread = conversation.unreadCount ?? 0;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4 text-start transition duration-150 hover:-translate-y-px hover:border-primary-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
    >
      {image !== "" ? (
        <img
          src={ccAttachmentUrl(image)}
          alt=""
          aria-hidden="true"
          className="size-10 shrink-0 rounded-full object-cover"
        />
      ) : (
        <span
          aria-hidden="true"
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
        >
          <LuHeadset className="size-5" aria-hidden="true" />
        </span>
      )}
      <span className="flex min-w-px flex-1 flex-col gap-0.5">
        <span className="truncate text-base font-semibold text-black">
          {title}
        </span>
        <span className="truncate text-sm text-black/60">
          {previewPrefix !== "" ? `${previewPrefix}: ${preview}` : preview}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-2 self-start">
        <span className="whitespace-nowrap text-xs text-black/40">
          {formatWhen(when, locale)}
        </span>
        {unread > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-bold text-white">
            {unread}
            <span className="sr-only">
              {" "}
              {t("chat_unread", { count: unread })}
            </span>
          </span>
        )}
      </span>
    </button>
  );
};

export default ChatCard;
